import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { API_BASE_URL } from "../App";
import "../styles/MainPage.css";

const getCoachVotePoints = (coachRank) => {
  const rank = Number(coachRank) || 0;
  if (rank === 1) return 6;
  if (rank === 2) return 4;
  if (rank === 3) return 2;
  return 0;
};

const overallScore = (player) =>
  (Number(player.FirstChoiceVotes) || 0) * 3 +
  (Number(player.SecondChoiceVotes) || 0) * 2 +
  (Number(player.ThirdChoiceVotes) || 0) +
  getCoachVotePoints(player.CoachVotes);

function AllTournamentPage() {
  const [genderFilter, setGenderFilter] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("");
  const [players, setPlayers] = useState([]);
  const [rankedPlayers, setRankedPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionStatus, setActionStatus] = useState(null);

  const showStatus = (msg, isError = false) => {
    setActionStatus({ msg, isError });
    setTimeout(() => setActionStatus(null), 3000);
  };

  useEffect(() => {
    const fetchAllTournamentPlayers = async () => {
      if (!genderFilter || !divisionFilter) {
        setPlayers([]);
        setRankedPlayers([]);
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/all-tournament-players?gender=${encodeURIComponent(
            genderFilter
          )}&division=${encodeURIComponent(divisionFilter)}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPlayers(data);

        const withRanking = [...data].sort((a, b) => {
          const rankA = Number.isFinite(Number(a.ATTRanking)) ? Number(a.ATTRanking) : Number.MAX_SAFE_INTEGER;
          const rankB = Number.isFinite(Number(b.ATTRanking)) ? Number(b.ATTRanking) : Number.MAX_SAFE_INTEGER;
          if (rankA !== rankB) return rankA - rankB;
          return overallScore(b) - overallScore(a);
        });

        setRankedPlayers(withRanking);
      } catch (error) {
        console.error("Error loading All Tournament players:", error);
        setLoadError("Failed to load All Tournament players.");
        setPlayers([]);
        setRankedPlayers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllTournamentPlayers();
  }, [genderFilter, divisionFilter]);

  const rankedIds = useMemo(() => new Set(rankedPlayers.map((p) => p.PlayerXID)), [rankedPlayers]);

  const unrankedPlayers = useMemo(
    () => players.filter((player) => !rankedIds.has(player.PlayerXID)),
    [players, rankedIds]
  );

  const handleAddToRanked = (player) => {
    if (rankedIds.has(player.PlayerXID)) return;
    setRankedPlayers((prev) => [...prev, player]);
  };

  const handleRemoveFromRanked = async (playerXID) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player-details`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerXID, attRanking: null }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRankedPlayers((prev) => prev.filter((player) => player.PlayerXID !== playerXID));
      setPlayers((prev) =>
        prev.map((player) =>
          player.PlayerXID === playerXID ? { ...player, ATTRanking: null } : player
        )
      );
      showStatus("Player removed from ranking.");
    } catch (error) {
      console.error("Error removing player ranking:", error);
      showStatus("Failed to clear player ranking.", true);
    }
  };

  const handleRemoveCandidate = async (playerXID) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/player-details`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerXID, allTournament: false, attRanking: null }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRankedPlayers((prev) => prev.filter((player) => player.PlayerXID !== playerXID));
      setPlayers((prev) => prev.filter((player) => player.PlayerXID !== playerXID));
      showStatus("Player removed from All Tournament candidates.");
    } catch (error) {
      console.error("Error removing candidate:", error);
      showStatus("Failed to remove candidate.", true);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = Array.from(rankedPlayers);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setRankedPlayers(reordered);
  };

  const handlePushRanking = async () => {
    if (rankedPlayers.length === 0) return;

    const rankings = rankedPlayers.map((player, index) => ({
      playerXID: player.PlayerXID,
      rank: index + 1,
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/all-tournament-ranking`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rankings }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setPlayers((prev) =>
        prev.map((player) => {
          const found = rankings.find((ranking) => ranking.playerXID === player.PlayerXID);
          return found ? { ...player, ATTRanking: found.rank } : player;
        })
      );

      showStatus("ATT ranking saved to server.");
    } catch (error) {
      console.error("Error saving ATT ranking:", error);
      showStatus("Failed to push rankings.", true);
    }
  };

  return (
    <div className="att-page-layout">
      <div className="left-pane">
        <label>Gender</label>
        <div className="gender-filters">
          <button
            className={genderFilter === "M" ? "active" : ""}
            onClick={() => setGenderFilter((prev) => (prev === "M" ? "" : "M"))}
          >
            Male
          </button>
          <button
            className={genderFilter === "W" ? "active" : ""}
            onClick={() => setGenderFilter((prev) => (prev === "W" ? "" : "W"))}
          >
            Female
          </button>
        </div>

        <label>Division</label>
        <select value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)}>
          <option value="">All</option>
          <option value="I">DI</option>
          <option value="IAA">DIAA</option>
          <option value="IAAA">DIAAA</option>
          <option value="II">DII</option>
          <option value="IIAA">DIIAA</option>
          <option value="IIAAA">DIIAAA</option>
          <option value="III">DIII</option>
          <option value="IIIAA">DIIIAA</option>
        </select>

        {!genderFilter || !divisionFilter ? (
          <div className="empty-team-state">Select gender and division to load players.</div>
        ) : loading ? (
          <div className="team-loading">Loading All Tournament players...</div>
        ) : loadError ? (
          <div className="team-load-error">{loadError}</div>
        ) : (
          <div className="att-player-list">
            {[...players]
              .sort((a, b) => overallScore(b) - overallScore(a))
              .map((player) => {
                const isRanked = rankedIds.has(player.PlayerXID);
                return (
                  <div key={player.PlayerXID} className={`att-player-card ${isRanked ? "in-ranking" : ""}`}>
                    <div className="att-player-details">
                      <strong>
                        #{player.JerseyNumber} {player.LegalFirstName} {player.LastName}
                      </strong>
                      <div>{player.TeamName}</div>
                      <div>
                        {player.PositionName} • Score: {overallScore(player)}
                      </div>
                    </div>
                    <div className="att-player-actions">
                      <button
                        className="toolbar-btn toolbar-btn-att att-icon-btn"
                        disabled={isRanked}
                        onClick={() => handleAddToRanked(player)}
                        title="Add to ranking"
                        aria-label="Add to ranking"
                      >
                        +
                      </button>
                      <button
                        className="toolbar-btn toolbar-btn-clear att-icon-btn"
                        onClick={() => handleRemoveCandidate(player.PlayerXID)}
                        title="Remove candidate"
                        aria-label="Remove candidate"
                      >
                        -
                      </button>
                    </div>
                  </div>
                );
              })}
            {unrankedPlayers.length === 0 && players.length > 0 && (
              <div className="empty-team-state">All players are currently in the ranking pane.</div>
            )}
          </div>
        )}
      </div>

      <div className="right-pane">
        <div className="action-toolbar">
          <span className="selection-count">Ranked: {rankedPlayers.length}</span>
          <button className="toolbar-btn toolbar-btn-att" onClick={handlePushRanking} disabled={rankedPlayers.length === 0}>
            Push to Server
          </button>
          {actionStatus && (
            <span className={`action-status ${actionStatus.isError ? "status-error" : "status-success"}`}>
              {actionStatus.msg}
            </span>
          )}
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="att-ranked-players">
            {(provided) => (
              <div className="att-ranking-list" ref={provided.innerRef} {...provided.droppableProps}>
                {rankedPlayers.length === 0 && (
                  <div className="empty-team-state">Add players from the left pane to start ranking.</div>
                )}

                {rankedPlayers.map((player, index) => (
                  <Draggable
                    key={player.PlayerXID}
                    draggableId={String(player.PlayerXID)}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <div
                        className="att-rank-item"
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <div className="att-rank-meta">
                          <span className="att-rank-index">{index + 1}</span>
                          <div>
                            <strong>
                              #{player.JerseyNumber} {player.LegalFirstName} {player.LastName}
                            </strong>
                            <div>
                              {player.TeamName} • {player.PositionName} • Score: {overallScore(player)}
                            </div>
                          </div>
                        </div>
                        <button
                          className="toolbar-btn toolbar-btn-clear"
                          onClick={() => handleRemoveFromRanked(player.PlayerXID)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}

export default AllTournamentPage;
